/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable, of, ReplaySubject, zip } from 'rxjs';
import { tap, map, mergeMap, catchError, take } from 'rxjs/operators';
import merge from 'deepmerge';
import { get, isObject, set } from 'lodash';
import {
    BrowserType,
    CommonDeviceType,
    DeviceOSType,
    parseVersion,
    isAtLeastVersion
} from 'crimson/lib/platform-detector/platform-detector';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { SystemInfoKey } from 'crimson/lib/system-info-service/system-info.models';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { PluginsNotificationService } from 'plugins-notification/lib/plugins-notification.service';
import { SystemInfoPluginKey } from 'plugins-notification/lib/interfaces/plugins-notification.interfaces';

export interface BuildConfiguration {
    version: string;
    type: string;
    branch: string;
    hash: string;
    variant: string;
}

/**
 * GFNProductConfiguration contains the product version of native code, this value will replace the build version in some specific cases
 * if the page works under hybrid mode, such as Android Angular client. The product version is initialized at the beginning of the page
 * been loaded, and can be retrieved by function getProductVerion.
 *
 * The product version required, but not limited to:
 * 1. lcars query header nv-client-version
 * 2. telemetry events productVersion, unless featureEnablement.preferShellVersion is set
 * 3. the version string of setting->about
 *
 * Vega does not set ProductVersion. The RN shell version is SystemInfo.ShellVersion
 * and is applied only by telemetry. Other callers keep config.build.version.
 */
export interface GFNProductConfiguration {
    version?: string;
}

export interface RetryConfiguation {
    retryAttempts: string;
    interval: string;
}

export interface BaseConfigType {
    build?: BuildConfiguration;
    product?: GFNProductConfiguration;
    [key: string]: any;
}

export interface GXTRemoteOverrideParam {
    name: string;
    value: any;
    version: string;
}

export interface GXTRemoteOverrideDebugInfo {
    revision: string;
}

export interface GXTRemoteOverrides {
    params?: GXTRemoteOverrideParam[];
    debugInfo?: GXTRemoteOverrideDebugInfo;
}

/* RCParamNameToConfigKeyPathMapping is the mapping of remote config paramater name in GXT RC Admin Panel to local config ( config present in config.json) key's path.
   This mapping needs to be updated whenever we introduce new paramter in GXT RC Admin Panel and we want it to override the local config key's value

   You can target local config key's path using . operator also

   e.g
   1] ecom: 'featureEnablement.ecommerce'
   2] starfleetPortNumber: 'starfleet.portNumbers'
   3] lcarsServer: lcars

   Here, ecom , starfleetPortNumber, lcarsServer are remote config parameter's name
   & featureEnablement.ecommerce , starfleet.portNumbers, lcars are paths of local config key's within config.json
   which we want to override with remote config paramater
*/
export interface RCParamNameToConfigKeyPathMapping {
    [k: string]: string;
}

@Injectable()
export class AppConfigParameters<ConfigType extends BaseConfigType> {
    filePath?: string;
    config?: ConfigType; // If present will use instead of loading from file
    production?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AppConfig<ConfigType extends BaseConfigType> {
    private config: ConfigType;
    private variant: Partial<ConfigType>;
    private overrides: Partial<ConfigType>;
    private cvConfigOverrides: Partial<ConfigType>;
    private piplLocalizationConfigOverrides: Partial<ConfigType>;
    private remoteOverridesFromGXT: GXTRemoteOverrides;
    private rcParamNameToConfigKeyPathMapping: RCParamNameToConfigKeyPathMapping;
    private logger: Logger;
    // This is used to notify other APP_INITIALIZERs that
    // may have dependencies on the Configuration blob
    private configFetchedSubject: ReplaySubject<ConfigType> = new ReplaySubject(1);
    private browserStaticSystemInfo$: Observable<{
        OSName: string;
        DeviceType: string;
        ProductVersion: string;
        BrowserName: string;
        BrowserVersion: string;
        SystemName: string;
    }>;
    private cefStaticSystemInfo$: Observable<{
        Platform: DeviceOSType;
    }>;
    private http: HttpClient;
    private systemInfoService: SystemInfoService;

    constructor(
        private httpBackend: HttpBackend,
        loggingService: LoggingService,
        private params: AppConfigParameters<ConfigType>,
        private injector: Injector,
        private pluginsNotificationService: PluginsNotificationService
    ) {
        this.logger = loggingService.getLogger('app.config');
        this.http = new HttpClient(this.httpBackend);
        this.cefStaticSystemInfo$ = of(null);

        // Adding subscriptions from SystemInfo to determine which specific config should be loaded for browser client.
        if (PlatformDetectorService.isBrowserLikePlatform()) {
            // Fetching systeminfo is specific to browser only so injected
            // for browser only to avoid calling systeminfo at early phase for nvapp
            this.systemInfoService = this.injector.get(SystemInfoService);
            if (this.systemInfoService) {
                this.browserStaticSystemInfo$ = this.systemInfoService
                    .getSystemInfo([
                        SystemInfoKey.OSName,
                        SystemInfoKey.DeviceType,
                        SystemInfoKey.ProductVersion,
                        SystemInfoKey.BrowserName,
                        SystemInfoKey.BrowserVersion,
                        SystemInfoKey.SystemName,
                        SystemInfoKey.VendorName
                    ])
                    .pipe(
                        take(1),
                        map(systemInfo => {
                            const result = {
                                OSName: systemInfo.OSName,
                                DeviceType: systemInfo.DeviceType,
                                BrowserName: systemInfo.BrowserName,
                                BrowserVersion: systemInfo.BrowserVersion,
                                ProductVersion: systemInfo.ProductVersion,
                                SystemName: systemInfo.SystemName
                            };
                            return result;
                        })
                    );
            } else {
                this.browserStaticSystemInfo$ = of(null);
            }
        } else {
            this.browserStaticSystemInfo$ = of(null);
        }

        /* Platform-check-disable-next-line */
        // Using for applying OS based overrides for CEF clients
        if (PlatformDetectorService.isCefPlatform()) {
            this.systemInfoService = this.injector.get(SystemInfoService);
            if (this.systemInfoService) {
                this.cefStaticSystemInfo$ = this.pluginsNotificationService.isPluginLoaded$(SystemInfoPluginKey).pipe(
                    mergeMap(isPluginLoaded => {
                        if (isPluginLoaded) {
                            return this.systemInfoService.getDeviceOSType();
                        } else {
                            return of(null);
                        }
                    }),
                    map(value => ({
                        Platform: value
                    }))
                );
            }
        }
    }

    public get configFetched$(): Observable<ConfigType> {
        return this.configFetchedSubject.asObservable();
    }

    private mergeRemoteOverridesFromGXT(config: Partial<ConfigType>): Partial<ConfigType> {
        try {
            Object.keys(this.rcParamNameToConfigKeyPathMapping)?.forEach(rcParamName => {
                const localConfigKeyPath = this.rcParamNameToConfigKeyPathMapping[rcParamName];
                const rcParam = this.remoteOverridesFromGXT?.params?.find(param => param.name === rcParamName);
                if (rcParam) {
                    let rcParamValue = rcParam.value;
                    if (isObject(rcParamValue)) {
                        const localConfigKeyValue = get(config, localConfigKeyPath, {});
                        // Special case when we want to use rconfig directly without merging into local config.
                        // Can be used when datatypes are complex eg. {prop1: [], prop2: []}
                        if (!(rcParamValue as any).useRCConfigWithoutMerge) {
                            rcParamValue = merge(localConfigKeyValue, rcParamValue);
                        }
                    }
                    set(config, localConfigKeyPath, rcParamValue);
                }
            });
            return config;
        } catch (err) {
            this.logger.info('error in mergeRemoteOverridesFromGXT', err);
            return config;
        }
    }

    public get(): ConfigType {
        const cvConfigOverrides = this.cvConfigOverrides || {};
        const overrideConfig = this.overrides || {};
        const localizationConfigOverrides = this.piplLocalizationConfigOverrides || {};

        let variantConfig = {};
        if (this.config?.build?.type === 'dev') {
            variantConfig = this.variant || {};
        }

        const configWithVariantOverridesMerged = merge(this.config, variantConfig);

        let configWithRemoteOverridesMerged = configWithVariantOverridesMerged;
        if (this.remoteOverridesFromGXT) {
            configWithRemoteOverridesMerged = this.mergeRemoteOverridesFromGXT(configWithVariantOverridesMerged);
        }

        let configWithCvOverridesMerged = configWithRemoteOverridesMerged;
        if (cvConfigOverrides) {
            configWithCvOverridesMerged = merge(configWithRemoteOverridesMerged, cvConfigOverrides);
        }

        return merge(merge(configWithCvOverridesMerged, localizationConfigOverrides), overrideConfig) as ConfigType;
    }

    public setCvConfigOverrides(cvConfigOverrides: Partial<ConfigType>): void {
        this.cvConfigOverrides = cvConfigOverrides;
    }

    public setOverrides(overrides: Partial<ConfigType>): void {
        this.overrides = overrides;
    }

    public setPiplLocalizationConfigOverrides(overrides: Partial<ConfigType>): void {
        this.piplLocalizationConfigOverrides = overrides;
    }

    public setRemoteOverridesFromGXT(
        overrides: GXTRemoteOverrides,
        rcParamNameToConfigKeyPathMapping: RCParamNameToConfigKeyPathMapping
    ): void {
        this.rcParamNameToConfigKeyPathMapping = rcParamNameToConfigKeyPathMapping;
        this.remoteOverridesFromGXT = overrides;
    }

    private getConfigFromHttp = (): Observable<ConfigType> =>
        this.http.get<ConfigType>(this.params.filePath).pipe(
            tap(
                response => {
                    this.logger.info('Successfully read application config file', response.build);
                    return response;
                },
                error => {
                    this.logger.error(`Configuration file ${this.params.filePath} could not be read. Error: ${error}`);
                }
            )
        );

    private parseEncodedConfig(encodedConfigVal: string): ConfigType {
        if (encodedConfigVal && encodedConfigVal.trim() !== '') {
            try {
                return JSON.parse(atob(encodedConfigVal));
            } catch {
                return null;
            }
        }
        return null;
    }

    private getConfig = (): Observable<ConfigType> => {
        const gfnAppConfig = document.getElementsByTagName('gfn-app-config')[0];
        const encodedConfigVal = gfnAppConfig?.getAttribute('data-value');
        gfnAppConfig?.remove(); // remove the gfnAppConfig element after data-value is read.

        const parsedConfig: ConfigType = this.parseEncodedConfig(encodedConfigVal);
        // check if config exits in index.html else get the config using http call.
        if (parsedConfig) {
            return of(parsedConfig);
        } else {
            return this.getConfigFromHttp();
        }
    };

    public load(): Observable<ConfigType> {
        if (this.params.config) {
            this.logger.info('Initializing config with provided data');
            this.config = this.params.config;
            return of(this.config);
        } else if (this.params.filePath) {
            this.logger.info(`Attempting to read application config file ${this.params.filePath}`);
            return zip(this.getConfig(), this.browserStaticSystemInfo$, this.cefStaticSystemInfo$).pipe(
                tap(
                    responses => {
                        this.config = responses[0];
                    },
                    error => {
                        this.logger.error(`Some problem while executing getConfig function. Error: ${error}`);
                    }
                ),
                tap(([_, browserSystemInfo, cefSystemInfo]) => {
                    const mergeIfExists = (c: any) => {
                        if (c) {
                            this.config = merge(this.config, c) as ConfigType;
                        }
                    };

                    // override the browser configuration settings
                    if (this.config && browserSystemInfo) {
                        const deviceType = browserSystemInfo.DeviceType;
                        const osType = browserSystemInfo.OSName;
                        const productVersion = browserSystemInfo.ProductVersion;
                        const browserName = browserSystemInfo.BrowserName;
                        const browserVersion = browserSystemInfo.BrowserVersion;
                        const systemName = browserSystemInfo.SystemName;
                        this.logger.info(
                            `DeviceType: ${deviceType}, DeviceOSType: ${osType}, ProductVersion: ${productVersion}, BrowserVersion: ${browserVersion}, SystemName: ${systemName}`
                        );
                        mergeIfExists(this.get().browserConfig);

                        const isMobile =
                            /* Platform-check-disable-next-line */
                            deviceType === CommonDeviceType.Phone || deviceType === CommonDeviceType.Tablet;
                        const isTV = deviceType === CommonDeviceType.TV;
                        const isConsole = deviceType === CommonDeviceType.Console && osType !== DeviceOSType.SteamOS;
                        /* Platform-check-disable-next-line */
                        const isHmd = deviceType === CommonDeviceType.HMD;
                        const isAndroid = osType === DeviceOSType.Android;
                        const isFirefox = browserName === BrowserType.FIREFOX;
                        const isMacSafari = osType === DeviceOSType.MacOS && browserName === BrowserType.SAFARI;
                        /*
                            ToDo: Cleanup of Config Merging Logic (AVENGERS-2854)
                        */
                        switch (osType) {
                            case DeviceOSType.IOS:
                                mergeIfExists(this.get().iOSConfig);
                                break;
                            case DeviceOSType.IPadOS:
                                mergeIfExists(this.get().iPadOSConfig);
                                break;
                            case DeviceOSType.Android:
                                // Android Browser Client
                                if (PlatformDetectorService.isBrowserPlatform()) {
                                    if (deviceType === CommonDeviceType.Phone) {
                                        mergeIfExists(this.get().androidPhoneConfig);
                                    } else if (deviceType === CommonDeviceType.Tablet) {
                                        mergeIfExists(this.get().androidTabletConfig);
                                    }
                                } else {
                                    mergeIfExists(this.get().androidConfig);
                                }
                                break;
                            case DeviceOSType.Shield:
                                mergeIfExists(this.get().androidConfig);
                                mergeIfExists(this.get().shieldConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // FireOS will be treated exactly like standard Android
                            case DeviceOSType.FireOS:
                                mergeIfExists(this.get().androidConfig);
                                mergeIfExists(this.get().fireOsConfig);
                                break;
                            case DeviceOSType.Tizen:
                                mergeIfExists(this.get().tizenConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // WebOS check to merge config
                            case DeviceOSType.WebOS:
                                mergeIfExists(this.get().webOSConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // VegaOS (Amazon Fire TV) specific config
                            case DeviceOSType.VegaOS:
                                mergeIfExists(this.get().vegaOSConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // To merge steam specific config
                            case DeviceOSType.SteamOS:
                                mergeIfExists(this.get().steamOSConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // To merge visionOS specific config
                            case DeviceOSType.VisionOS:
                                mergeIfExists(this.get().visionOSConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // To merge AluminiumOS specific config
                            case DeviceOSType.AluminiumOS:
                                if (PlatformDetectorService.isBrowserPlatform()) {
                                    mergeIfExists(this.get().aluminiumOSConfig);
                                } else {
                                    mergeIfExists(this.get().androidConfig);
                                    mergeIfExists(this.get().aluminiumOSConfig);
                                }
                                break;
                        }

                        if (isMobile) {
                            mergeIfExists(this.get().mobileConfig);
                        } else if (isTV) {
                            // Android Browser Client
                            if (PlatformDetectorService.isBrowserPlatform() && isAndroid) {
                                mergeIfExists(this.get().androidTvConfig);
                            } else {
                                // Please be cautious when overriding the generic config here, as OS-specific config is merged first.
                                mergeIfExists(this.get().tvConfig);
                            }
                        } else if (isConsole) {
                            mergeIfExists(this.get().consoleConfig);
                        } else if (isHmd) {
                            /* Platform-check-disable-next-line */
                            // Pick hmdConfig based on whether it's a browser Android platform or not.
                            if (PlatformDetectorService.isBrowserPlatform() && isAndroid) {
                                mergeIfExists(this.get().androidHmdConfig);
                            } else {
                                mergeIfExists(this.get().hmdConfig);
                            }
                        }

                        if (isMacSafari) {
                            mergeIfExists(this.get().macSafariConfig);
                            // MVPC-14651: Safari 26.4+ supports Keyboard Lock via requestFullscreen({ keyboardLock: 'browser' }).
                            // Suppress hotkey guidance since Escape works natively with keyboard lock.
                            if (this.isSafariKeyboardLockSupported(browserVersion)) {
                                this.logger.info('Safari KeyboardLock supported: suppressing hotkey guidance config');
                                this.config = merge(this.config, {
                                    client: {
                                        requiresHotkeyMessage: false,
                                        overrideEscKeyDuringGameplay: false
                                    }
                                }) as ConfigType;
                            }
                        }
                        if (isFirefox) {
                            mergeIfExists(this.get().firefoxConfig);
                            if (osType === DeviceOSType.MacOS) {
                                mergeIfExists(this.get().macFirefoxConfig);
                            }
                        }

                        // Merge the product version from system info if the value exists.
                        if (productVersion) {
                            this.config = merge(this.config, { product: { version: productVersion } }) as ConfigType;
                        }
                    } else if (this.config && cefSystemInfo) {
                        const deviceOSType = cefSystemInfo.Platform;
                        switch (deviceOSType) {
                            /* Platform-check-disable-next-line */
                            // Applying SteamOS specific overrides
                            case DeviceOSType.SteamOS:
                                const steamOSConfig = this.get().steamOSConfig;
                                mergeIfExists(steamOSConfig);
                                break;
                            /* Platform-check-disable-next-line */
                            // To merge linux specific config
                            case DeviceOSType.Linux:
                                const linuxOSConfig = this.get().linuxOSConfig;
                                mergeIfExists(linuxOSConfig);
                                break;
                        }
                    }
                }),
                mergeMap(responses => this.loadVariant(responses[0])),
                map(() => {
                    const configData = this.get();
                    this.logger.info('appConfig loaded');
                    this.configFetchedSubject.next(configData);
                    return configData;
                })
            );
        } else {
            this.logger.error('No valid data source specified in the AppConfigParameters');
            return of(undefined);
        }
    }

    private isSafariKeyboardLockSupported(version: string): boolean {
        const parsed = parseVersion(version);
        return !!parsed && isAtLeastVersion(parsed, [26, 4, 0]);
    }

    private loadVariant(config: ConfigType): Observable<ConfigType> {
        const variant = config?.build?.variant;
        const type = config?.build?.type;
        if (!variant || type !== 'dev') {
            return of(config);
        }

        const variantFilePath =
            this.params.filePath.replace(/\.[^/\\.]+$/, '.') + variant + '.' + this.params.filePath.split('.').pop();
        return this.http.get<ConfigType>(variantFilePath).pipe(
            tap(response => {
                this.logger.info(`Successfully read ${variant} variant config`);
                this.variant = response;
            }),
            catchError(() => of(config))
        );
    }
}
